import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { useSocket, useUnreadCounts, markRoomAsRead, setInitialUnread, incrementRoomUnread } from '../../hooks/useSocket';
import Avatar from '../../components/Avatar';
import { 
  Send, 
  MessageCircle, 
  Users, 
  ChevronLeft,
  Plus,
  BellOff,
  Bell,
  X,
  User,
  Check,
  Wifi,
  WifiOff,
  Trash2,
  UserPlus,
  MoreVertical,
  Archive,
  Pin,
  MailOpen
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

const EMOJI_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

function MessageBubble({ message, isOwn, onReact, onDelete, canDelete }) {
  const [showReactions, setShowReactions] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formatTime = (date) => {
    const d = new Date(date);
    if (isToday(d)) return format(d, 'h:mm a');
    if (isYesterday(d)) return 'Yesterday ' + format(d, 'h:mm a');
    return format(d, 'MMM d, h:mm a');
  };

  const groupedReactions = message.reactions?.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = [];
    acc[r.emoji].push(r.user);
    return acc;
  }, {}) || {};

  const handleDelete = async () => {
    if (!confirm('Delete this message?')) return;
    setDeleting(true);
    await onDelete(message.id);
    setDeleting(false);
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 group`}>
      {!isOwn && (
        <div className="mr-2 flex-shrink-0">
          <Avatar name={message.sender.displayName || message.sender.name} photo={message.sender.profilePhoto} size="sm" />
        </div>
      )}
      <div className={`max-w-[80%] ${isOwn ? 'order-2' : ''}`}>
        {!isOwn && (
          <p className="text-xs text-gray-500 mb-1 ml-1">{message.sender.displayName || message.sender.name}</p>
        )}
        <div className="flex items-center gap-1">
          {isOwn && canDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
              title="Delete message"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          
          <div 
            className={`relative rounded-2xl px-4 py-2 ${
              isOwn 
                ? 'bg-hub-500 text-white rounded-br-md' 
                : 'bg-gray-100 text-gray-900 rounded-bl-md'
            }`}
            onDoubleClick={() => setShowReactions(!showReactions)}
          >
            <p className="whitespace-pre-wrap break-words text-lg">{message.content}</p>
            <p className={`text-xs mt-1 ${isOwn ? 'text-hub-200' : 'text-gray-400'}`}>
              {formatTime(message.createdAt)}
            </p>

            {showReactions && (
              <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} -top-10 bg-white shadow-lg rounded-full px-2 py-1 flex gap-1 z-10`}>
                {EMOJI_OPTIONS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onReact(message.id, emoji);
                      setShowReactions(false);
                    }}
                    className="hover:scale-125 transition-transform text-lg"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {Object.keys(groupedReactions).length > 0 && (
          <div className={`flex gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(groupedReactions).map(([emoji, users]) => (
              <span 
                key={emoji} 
                className="bg-gray-100 rounded-full px-2 py-0.5 text-sm cursor-pointer hover:bg-gray-200"
                title={users.map(u => u.displayName || u.name).join(', ')}
                onClick={() => onReact(message.id, emoji)}
              >
                {emoji} {users.length > 1 && users.length}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator({ typingUsers }) {
  if (typingUsers.length === 0) return null;

  const text = typingUsers.length === 1
    ? `${typingUsers[0].displayName || typingUsers[0].name} is typing...`
    : typingUsers.length === 2
    ? `${typingUsers[0].displayName || typingUsers[0].name} and ${typingUsers[1].displayName || typingUsers[1].name} are typing...`
    : `${typingUsers[0].displayName || typingUsers[0].name} and ${typingUsers.length - 1} others are typing...`;

  return (
    <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{text}</span>
    </div>
  );
}

function AddMembersModal({ room, onClose, onAdded, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      let data;
      try {
        data = await api.get('/chat/users');
      } catch {
        data = await api.get('/contractors?active=true');
      }
      const existingMemberIds = room.members?.map(m => m.userId) || [];
      setUsers(data.filter(u => u.id !== currentUserId && !existingMemberIds.includes(u.id)));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAdd = async () => {
    if (selectedUsers.length === 0) return;
    setAdding(true);
    try {
      await api.post(`/chat/rooms/${room.id}/members`, { memberIds: selectedUsers });
      onAdded();
    } catch (err) {
      alert(err.message);
    }
    setAdding(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add Members to {room.name}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No more users to add</p>
          ) : (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-1">
                {users.map(user => (
                  <button
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    className={`w-full p-3 rounded-lg flex items-center justify-between ${
                      selectedUsers.includes(user.id) ? 'bg-hub-50 border-hub-200' : 'hover:bg-gray-50'
                    } border`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                        {(user.displayName || user.name).charAt(0)}
                      </div>
                      <span>{user.displayName || user.name}</span>
                    </div>
                    {selectedUsers.includes(user.id) && (
                      <Check className="w-5 h-5 text-hub-600" />
                    )}
                  </button>
                ))}
              </div>

              {selectedUsers.length > 0 && (
                <button 
                  onClick={handleAdd} 
                  disabled={adding}
                  className="btn-primary w-full"
                >
                  {adding ? 'Adding...' : `Add ${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewChatModal({ onClose, onCreated, currentUserId, currentUserRole }) {
  const [step, setStep] = useState('type');
  const [chatType, setChatType] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const isAdmin = ['OWNER', 'ADMIN'].includes(currentUserRole);

  useEffect(() => {
    if (step === 'select') {
      loadUsers();
    }
  }, [step]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      let data;
      try {
        data = await api.get('/chat/users');
      } catch {
        data = await api.get('/contractors?active=true');
      }
      
      // Filter out current user
      let filtered = data.filter(u => u.id !== currentUserId);
      
      // If current user is a contractor, only show:
      // 1. ADMIN/OWNER users (always visible)
      // 2. Other contractors who have shareContactInfo enabled
      if (!isAdmin) {
        filtered = filtered.filter(u => 
          ['OWNER', 'ADMIN', 'ACCOUNTANT'].includes(u.role) || u.shareContactInfo === true
        );
      }
      
      setUsers(filtered);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleUser = (userId) => {
    if (chatType === 'direct') {
      setSelectedUsers([userId]);
    } else {
      setSelectedUsers(prev => 
        prev.includes(userId) 
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    }
  };

  const handleCreate = async () => {
    if (chatType === 'direct' && selectedUsers.length !== 1) return;
    if (chatType === 'group' && (!groupName.trim() || selectedUsers.length === 0)) return;

    setCreating(true);
    try {
      const selectedUser = users.find(u => u.id === selectedUsers[0]);
      const payload = {
        type: chatType === 'direct' ? 'DIRECT' : 'GROUP',
        name: chatType === 'direct' 
          ? (selectedUser?.displayName || selectedUser?.name || 'Direct Message')
          : groupName.trim(),
        memberIds: selectedUsers
      };

      const room = await api.post('/chat/rooms', payload);
      onCreated(room);
    } catch (err) {
      alert(err.message);
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Chat</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {step === 'type' && (
            <div className="space-y-3">
              <button
                onClick={() => { setChatType('direct'); setStep('select'); }}
                className="w-full p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3 text-left"
              >
                <User className="w-8 h-8 text-hub-500" />
                <div>
                  <p className="font-medium">Direct Message</p>
                  <p className="text-sm text-gray-500">Private chat with one person</p>
                </div>
              </button>
              <button
                onClick={() => { setChatType('group'); setStep('select'); }}
                className="w-full p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3 text-left"
              >
                <Users className="w-8 h-8 text-hub-500" />
                <div>
                  <p className="font-medium">Group Chat</p>
                  <p className="text-sm text-gray-500">Chat with multiple people</p>
                </div>
              </button>
            </div>
          )}

          {step === 'select' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={() => { setStep('type'); setSelectedUsers([]); }} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-medium">
                  {chatType === 'direct' ? 'Select Person' : 'Select Members'}
                </span>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No other users available</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => toggleUser(user.id)}
                      className={`w-full p-3 rounded-lg flex items-center justify-between ${
                        selectedUsers.includes(user.id) ? 'bg-hub-50 border-hub-200' : 'hover:bg-gray-50'
                      } border`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                          {(user.displayName || user.name).charAt(0)}
                        </div>
                        <span>{user.displayName || user.name}</span>
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <Check className="w-5 h-5 text-hub-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {chatType === 'direct' && selectedUsers.length === 1 && (
                <button 
                  onClick={handleCreate} 
                  disabled={creating}
                  className="btn-primary w-full"
                >
                  {creating ? 'Creating...' : 'Start Chat'}
                </button>
              )}

              {chatType === 'group' && selectedUsers.length > 0 && (
                <button 
                  onClick={() => setStep('name')}
                  className="btn-primary w-full"
                >
                  Next ({selectedUsers.length} selected)
                </button>
              )}
            </div>
          )}

          {step === 'name' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={() => setStep('select')} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-medium">Name Your Group</span>
              </div>

              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Group name..."
                className="input"
                autoFocus
              />

              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(userId => {
                  const user = users.find(u => u.id === userId);
                  return (
                    <span key={userId} className="px-2 py-1 bg-gray-100 rounded-full text-sm">
                      {user?.displayName || user?.name}
                    </span>
                  );
                })}
              </div>

              <button 
                onClick={handleCreate} 
                disabled={creating || !groupName.trim()}
                className="btn-primary w-full"
              >
                {creating ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SwipeableRoom({ room, selectedRoom, onSelectRoom, onDeleteRoom, onArchiveRoom, onPinRoom, onMuteRoom, onMarkUnread, currentUserId, unread }) {
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(false);
  const longPressTimer = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const DELETE_THRESHOLD = 80; // px to swipe to reveal archive

  // Helper to get the other member in a DM
  const getOtherMember = () => {
    if (room.type !== 'DIRECT' || !room.members) return null;
    return room.members.find(m => m.userId !== currentUserId)?.user || 
           room.members.find(m => m.user?.id !== currentUserId)?.user;
  };

  const otherMember = getOtherMember();
  const isCreator = room.isCreator || room.createdById === currentUserId;

  // Use useEffect to attach touch events with { passive: false } for Safari
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const onTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      isHorizontalSwipe.current = false;
      setIsSwiping(false);
      
      // Start long press timer for context menu
      longPressTimer.current = setTimeout(() => {
        // Open context menu on long press
        const rect = element.getBoundingClientRect();
        setMenuPosition({
          top: Math.min(rect.top + 40, window.innerHeight - 250),
          right: 20
        });
        setShowMenu(true);
        // Vibrate if supported
        if (navigator.vibrate) navigator.vibrate(50);
      }, 500);
    };

    const onTouchMove = (e) => {
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      // Cancel long press if moving
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }

      // Determine swipe direction on first significant movement
      if (!isHorizontalSwipe.current && !isSwiping) {
        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalSwipe.current = true;
        } else if (Math.abs(deltaY) > 10) {
          // Vertical scroll - let it go
          return;
        }
      }

      // Only handle horizontal swipes
      if (!isHorizontalSwipe.current) return;

      // Prevent vertical scroll when swiping horizontally
      e.preventDefault();

      // Only allow left swipe (negative deltaX)
      if (deltaX < 0) {
        setIsSwiping(true);
        const newX = Math.max(deltaX, -(DELETE_THRESHOLD + 20));
        setSwipeX(newX);
      } else if (swipeX < 0) {
        // Allow swiping back to close
        setIsSwiping(true);
        setSwipeX(Math.min(0, deltaX));
      }
    };

    const onTouchEnd = () => {
      // Cancel long press timer
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      
      if (Math.abs(swipeX) > DELETE_THRESHOLD / 2) {
        setSwipeX(-DELETE_THRESHOLD);
      } else {
        setSwipeX(0);
      }
      setIsSwiping(false);
      isHorizontalSwipe.current = false;
    };

    // Safari requires { passive: false } to allow preventDefault
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, [swipeX, isSwiping]);

  const handleClick = () => {
    if (isSwiping || Math.abs(swipeX) > 10 || showMenu) {
      setSwipeX(0);
      return;
    }
    onSelectRoom(room);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDeleteRoom(room);
    setSwipeX(0);
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onArchiveRoom(room);
    setSwipeX(0);
  };

  const handlePin = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onPinRoom(room);
  };

  const handleMute = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onMuteRoom(room);
  };

  const handleMarkUnread = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onMarkUnread(room);
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (!showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
    setShowMenu(!showMenu);
  };

  // Close menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setShowMenu(false);
      }
      if (containerRef.current && !containerRef.current.contains(e.target) && swipeX !== 0) {
        setSwipeX(0);
      }
    };
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [swipeX, showMenu]);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${selectedRoom?.id === room.id ? 'bg-hub-50' : ''}`}
    >
      {/* Archive button behind (mobile swipe) */}
      <div 
        className="absolute inset-y-0 right-0 flex items-center bg-amber-500 lg:hidden"
        style={{ width: DELETE_THRESHOLD + 20 }}
      >
        <button
          onClick={handleArchive}
          className="h-full w-full flex items-center justify-center text-white"
        >
          <Archive className="w-6 h-6" />
        </button>
      </div>

      {/* Swipeable content */}
      <div
        ref={contentRef}
        className="relative bg-white border-b border-gray-100 cursor-pointer select-none group"
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out',
          WebkitUserSelect: 'none',
          touchAction: 'pan-y pinch-zoom'
        }}
        onClick={handleClick}
      >
        <div className="p-4 flex items-center gap-3">
          {room.type === 'GROUP' ? (
            <div className="w-10 h-10 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
          ) : (
            <Avatar 
              name={otherMember?.displayName || otherMember?.name || room.name} 
              photo={otherMember?.profilePhoto} 
              size="md" 
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              {room.pinned && <Pin className="w-3 h-3 text-hub-500" />}
              <p className={`font-medium truncate ${unread > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                {room.type === 'DIRECT' && otherMember ? (otherMember.displayName || otherMember.name) : room.name}
              </p>
              {unread > 0 && (
                <span className="bg-hub-500 text-white text-xs rounded-full px-2 py-0.5 ml-auto">
                  {unread}
                </span>
              )}
            </div>
            {room.messages?.[0] && (
              <p className={`text-sm truncate ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                {room.messages[0].sender?.displayName || room.messages[0].sender?.name}: {room.messages[0].content}
              </p>
            )}
          </div>
          
          {/* Desktop context menu button */}
          <div className="relative hidden lg:block">
            <button
              ref={buttonRef}
              onClick={toggleMenu}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Context menu dropdown - fixed position to avoid clipping */}
      {showMenu && (
        <div 
          ref={menuRef}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]"
          style={{ top: menuPosition.top, right: menuPosition.right }}
        >
          <button
            onClick={handleMarkUnread}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <MailOpen className="w-4 h-4" />
            Mark as unread
          </button>
          <button
            onClick={handleArchive}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <Archive className="w-4 h-4" />
            Archive
          </button>
          <button
            onClick={handleMute}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            {room.muted ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            {room.muted ? 'Unmute notifications' : 'Mute notifications'}
          </button>
          <button
            onClick={handlePin}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <Pin className="w-4 h-4" />
            {room.pinned ? 'Unpin chat' : 'Pin chat'}
          </button>
          {isCreator && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={handleDelete}
                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RoomList({ rooms, selectedRoom, onSelectRoom, onNewChat, onDeleteRoom, onArchiveRoom, onPinRoom, onMuteRoom, onMarkUnread, currentUserId, showArchived, onToggleArchived }) {
  const { byRoom: unreadByRoom } = useUnreadCounts();

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Messages</h2>
        <div className="flex items-center gap-1">
          <button 
            onClick={onToggleArchived}
            className={`p-2 rounded-lg ${showArchived ? 'bg-hub-100 text-hub-600' : 'hover:bg-gray-100 text-gray-500'}`}
            title={showArchived ? 'Show active chats' : 'Show archived'}
          >
            <Archive className="w-5 h-5" />
          </button>
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onNewChat();
            }} 
            className="px-3 py-1.5 bg-hub-600 text-white text-sm font-medium rounded-lg hover:bg-hub-700 active:bg-hub-800 flex items-center gap-1" 
            title="New Chat"
          >
            <Plus className="w-4 h-4" />
            New
          </button>
        </div>
      </div>
      
      {showArchived && (
        <div className="px-4 py-2 bg-gray-50 border-b text-sm text-gray-600 flex items-center gap-2">
          <Archive className="w-4 h-4" />
          Viewing archived chats
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto">
        {rooms.map(room => {
          const unread = unreadByRoom[room.id] || room.unreadCount || 0;
          
          return (
            <SwipeableRoom
              key={room.id}
              room={room}
              selectedRoom={selectedRoom}
              onSelectRoom={onSelectRoom}
              onDeleteRoom={onDeleteRoom}
              onArchiveRoom={onArchiveRoom}
              onPinRoom={onPinRoom}
              onMuteRoom={onMuteRoom}
              onMarkUnread={onMarkUnread}
              currentUserId={currentUserId}
              unread={unread}
            />
          );
        })}
        {rooms.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            {showArchived ? (
              <p>No archived conversations</p>
            ) : (
              <>
                <p>No conversations yet</p>
                <button onClick={onNewChat} className="text-hub-600 hover:underline text-sm mt-1">
                  Start a new chat
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatView({ room, onBack, currentUser, onRoomUpdated }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [muted, setMuted] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(0);
  const textareaRef = useRef(null);
  
  const { 
    connected, 
    joinRoom, 
    leaveRoom, 
    startTyping, 
    stopTyping,
    onNewMessage, 
    onReactionUpdated,
    onUserTyping,
    onUserStoppedTyping
  } = useSocket();

  useEffect(() => {
    if (room?.id) {
      joinRoom(room.id);
      loadMessages();
      markRoomAsRead(room.id);
      
      const membership = room.members?.find(m => m.userId === currentUser.id);
      setMuted(membership?.muted || false);
    }
    
    return () => {
      if (room?.id) {
        leaveRoom(room.id);
        stopTyping(room.id);
      }
    };
  }, [room?.id]);

  useEffect(() => {
    const unsubscribe = onNewMessage(({ roomId, message }) => {
      if (roomId === room?.id) {
        setMessages(prev => {
          // Skip if we already have this message (by ID)
          if (prev.some(m => m.id === message.id)) return prev;
          
          // Skip if this is our own message - we added it optimistically
          // Check for temp messages with same content from us
          if (message.senderId === currentUser.id) {
            const hasTempVersion = prev.some(m => 
              m.id.startsWith('temp-') && 
              m.senderId === currentUser.id && 
              m.content === message.content
            );
            if (hasTempVersion) {
              // Replace temp with real message
              return prev.map(m => 
                (m.id.startsWith('temp-') && m.senderId === currentUser.id && m.content === message.content)
                  ? { ...message }
                  : m
              );
            }
          }
          
          return [...prev, message];
        });
        // Play sound for messages from others (if not muted)
        if (message.senderId !== currentUser.id && !muted) {
          const audio = new Audio('/notification.wav');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        }
        // Mark as read since we're viewing this room
        markRoomAsRead(room.id);
      }
    });
    return unsubscribe;
  }, [room?.id, onNewMessage, currentUser.id, muted]);

  useEffect(() => {
    const unsubscribe = onReactionUpdated(({ roomId, messageId, action, reaction, emoji, userId }) => {
      if (roomId === room?.id) {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== messageId) return msg;
          
          if (action === 'added') {
            return {
              ...msg,
              reactions: [...(msg.reactions || []), reaction]
            };
          } else {
            return {
              ...msg,
              reactions: (msg.reactions || []).filter(r => !(r.emoji === emoji && r.userId === userId))
            };
          }
        }));
      }
    });
    return unsubscribe;
  }, [room?.id, onReactionUpdated]);

  useEffect(() => {
    const unsubTyping = onUserTyping(({ roomId, user }) => {
      if (roomId === room?.id && user.id !== currentUser.id) {
        setTypingUsers(prev => {
          if (prev.some(u => u.id === user.id)) return prev;
          return [...prev, user];
        });
      }
    });

    const unsubStoppedTyping = onUserStoppedTyping(({ roomId, userId }) => {
      if (roomId === room?.id) {
        setTypingUsers(prev => prev.filter(u => u.id !== userId));
      }
    });

    return () => {
      unsubTyping();
      unsubStoppedTyping();
    };
  }, [room?.id, currentUser.id, onUserTyping, onUserStoppedTyping]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const data = await api.get(`/chat/rooms/${room.id}/messages`);
      setMessages(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Optimistic update - show message immediately
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      content,
      senderId: currentUser.id,
      sender: { 
        id: currentUser.id, 
        name: currentUser.name,
        displayName: currentUser.displayName,
        profilePhoto: currentUser.profilePhoto 
      },
      createdAt: new Date().toISOString(),
      reactions: []
    };
    
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      stopTyping(room.id);
      const savedMessage = await api.post(`/chat/rooms/${room.id}/messages`, { content });
      
      // Replace temp message with real one (socket might also fire, dedupe handles it)
      setMessages(prev => prev.map(m => 
        m.id === tempId ? { ...savedMessage, sender: optimisticMessage.sender } : m
      ));
    } catch (err) {
      console.error(err);
      // Remove optimistic message and restore input on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(content);
    }
    setSending(false);
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
    
    // Throttle typing notifications to once per second
    const now = Date.now();
    if (now - lastTypingRef.current > 1000) {
      startTyping(room.id);
      lastTypingRef.current = now;
    }

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(room.id);
    }, 2000);
  };

  const handleKeyDown = (e) => {
    // Send on Enter (without Shift), allow Shift+Enter for new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await api.post(`/chat/messages/${messageId}/react`, { emoji });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await api.delete(`/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleMute = async () => {
    try {
      await api.put(`/chat/rooms/${room.id}/mute`, { muted: !muted });
      setMuted(!muted);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMembersAdded = () => {
    setShowAddMembers(false);
    onRoomUpdated();
  };

  // Get other member for DM display
  const getOtherMember = () => {
    if (room.type !== 'DIRECT' || !room.members) return null;
    return room.members.find(m => m.userId !== currentUser.id)?.user ||
           room.members.find(m => m.user?.id !== currentUser.id)?.user;
  };

  const otherMember = getOtherMember();

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-3 lg:pt-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <button onClick={onBack} className="lg:hidden p-1 hover:bg-gray-100 rounded">
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        {room.type === 'DIRECT' ? (
          <Avatar 
            name={otherMember?.displayName || otherMember?.name || room.name} 
            photo={otherMember?.profilePhoto} 
            size="md" 
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">
            {room.type === 'DIRECT' && otherMember ? (otherMember.displayName || otherMember.name) : room.name}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{room.members?.length || 0} members</span>
            {connected ? (
              <span className="flex items-center gap-1 text-green-600">
                <Wifi className="w-3 h-3" /> Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-yellow-600">
                <WifiOff className="w-3 h-3" /> Connecting...
              </span>
            )}
          </div>
        </div>
        
        {room.type === 'GROUP' && (
          <button 
            onClick={() => setShowAddMembers(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Add members"
          >
            <UserPlus className="w-5 h-5 text-gray-600" />
          </button>
        )}
        
        <button 
          onClick={toggleMute}
          className="p-2 hover:bg-gray-100 rounded-lg"
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <BellOff className="w-5 h-5 text-gray-400" /> : <Bell className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwn={msg.senderId === currentUser.id}
                onReact={handleReact}
                onDelete={handleDelete}
                canDelete={msg.senderId === currentUser.id}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <TypingIndicator typingUsers={typingUsers} />

      <form onSubmit={handleSend} className="p-4 bg-white border-t border-gray-200">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Write something..."
            className="flex-1 input resize-none min-h-[44px] max-h-[120px] py-3"
            disabled={sending}
            rows={1}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="btn-primary p-3 flex-shrink-0"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1">Press Enter to send, Shift+Enter for new line</p>
      </form>

      {showAddMembers && (
        <AddMembersModal
          room={room}
          onClose={() => setShowAddMembers(false)}
          onAdded={handleMembersAdded}
          currentUserId={currentUser.id}
        />
      )}
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  const { onRoomUpdated, onRoomCreated } = useSocket();

  // Handle deep link - open specific room from URL query param
  useEffect(() => {
    const roomId = searchParams.get('room');
    if (roomId && rooms.length > 0 && !selectedRoom) {
      const targetRoom = rooms.find(r => r.id === roomId);
      if (targetRoom) {
        handleSelectRoom(targetRoom);
        // Clear the query param after navigating
        setSearchParams({});
      }
    }
  }, [searchParams, rooms, selectedRoom]);

  useEffect(() => {
    loadRooms();
  }, [showArchived]);

  useEffect(() => {
    const unsubscribe = onRoomUpdated(({ roomId, lastMessage }) => {
      // If viewing this room, don't increment - just update the message
      // The layout handles incrementing when NOT on chat page
      
      setRooms(prev => prev.map(room => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          messages: [{ ...lastMessage }],
          updatedAt: new Date().toISOString()
        };
      }).sort((a, b) => {
        // Keep pinned at top
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      }));
    });
    return unsubscribe;
  }, [onRoomUpdated]);

  useEffect(() => {
    const unsubscribe = onRoomCreated(() => {
      loadRooms();
    });
    return unsubscribe;
  }, [onRoomCreated]);

  const loadRooms = async () => {
    try {
      const data = await api.get(`/chat/rooms${showArchived ? '?archived=true' : ''}`);
      setRooms(data);
      
      // Initialize global unread counts from server
      const counts = {};
      data.forEach(room => {
        if (room.unreadCount > 0) {
          counts[room.id] = room.unreadCount;
        }
      });
      setInitialUnread(counts);
      
      if (data.length > 0 && window.innerWidth >= 1024 && !selectedRoom && !showArchived) {
        const fullRoom = await api.get(`/chat/rooms/${data[0].id}`);
        setSelectedRoom(fullRoom);
        markRoomAsRead(fullRoom.id);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSelectRoom = async (room) => {
    const fullRoom = await api.get(`/chat/rooms/${room.id}`);
    setSelectedRoom(fullRoom);
    markRoomAsRead(room.id);
  };

  const handleNewChatCreated = async (room) => {
    setShowNewChat(false);
    setShowArchived(false); // Switch back to active chats
    await loadRooms();
    const fullRoom = await api.get(`/chat/rooms/${room.id}`);
    setSelectedRoom(fullRoom);
  };

  const handleDeleteRoom = async (room) => {
    if (!confirm(`Delete conversation "${room.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/chat/rooms/${room.id}`);
      setRooms(prev => prev.filter(r => r.id !== room.id));
      if (selectedRoom?.id === room.id) {
        setSelectedRoom(null);
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete conversation');
    }
  };

  const handleArchiveRoom = async (room) => {
    try {
      await api.put(`/chat/rooms/${room.id}/archive`);
      setRooms(prev => prev.filter(r => r.id !== room.id));
      if (selectedRoom?.id === room.id) {
        setSelectedRoom(null);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to archive conversation');
    }
  };

  const handlePinRoom = async (room) => {
    try {
      const result = await api.put(`/chat/rooms/${room.id}/pin`);
      setRooms(prev => prev.map(r => 
        r.id === room.id ? { ...r, pinned: result.pinned } : r
      ).sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to pin conversation');
    }
  };

  const handleMuteRoom = async (room) => {
    try {
      const result = await api.put(`/chat/rooms/${room.id}/mute`);
      setRooms(prev => prev.map(r => 
        r.id === room.id ? { ...r, muted: result.muted } : r
      ));
    } catch (err) {
      console.error(err);
      alert('Failed to mute conversation');
    }
  };

  const handleMarkUnread = async (room) => {
    try {
      await api.put(`/chat/rooms/${room.id}/mark-unread`);
      // Reload to get updated unread counts
      loadRooms();
    } catch (err) {
      console.error(err);
      alert('Failed to mark as unread');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="flex h-full">
        <div className={`w-full lg:w-80 border-r border-gray-200 ${selectedRoom ? 'hidden lg:block' : ''}`}>
          <RoomList 
            rooms={rooms} 
            selectedRoom={selectedRoom}
            onSelectRoom={handleSelectRoom}
            onNewChat={() => setShowNewChat(true)}
            onDeleteRoom={handleDeleteRoom}
            onArchiveRoom={handleArchiveRoom}
            onPinRoom={handlePinRoom}
            onMuteRoom={handleMuteRoom}
            onMarkUnread={handleMarkUnread}
            currentUserId={user.id}
            showArchived={showArchived}
            onToggleArchived={() => setShowArchived(!showArchived)}
          />
        </div>

        <div className={`flex-1 ${!selectedRoom ? 'hidden lg:flex' : ''}`}>
          {selectedRoom ? (
            <ChatView 
              room={selectedRoom} 
              onBack={() => setSelectedRoom(null)}
              currentUser={user}
              onRoomUpdated={loadRooms}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Select a conversation</p>
                <button 
                  onClick={() => setShowNewChat(true)}
                  className="text-hub-600 hover:underline text-sm mt-2"
                >
                  or start a new chat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showNewChat && (
        <NewChatModal 
          onClose={() => setShowNewChat(false)}
          onCreated={handleNewChatCreated}
          currentUserId={user.id}
          currentUserRole={user.role}
        />
      )}
    </div>
  );
}
